/**
 * usePointerStroke (JSX)
 * ----------------------
 * Collects a freehand stroke as an array of {x,y} points in local SVG coords.
 * - Uses pointer capture + window listeners so movement is tracked reliably
 *   even when the pointer leaves the SVG.
 * - Throttles samples (sampleMs) to limit point density.
 *
 * API
 *   const { pts, start, move, finish, clear } = usePointerStroke(12)
 *   start(nativeEvent, targetEl)  // call in onPointerDown
 *   move(nativeEvent)             // (optional) if you also wire element onMove
 *   finish() -> Point[]           // call on pointer up/cancel; returns final stroke
 */

import { useEffect, useRef, useState } from "react";

export function usePointerStroke(sampleMs = 12) {
  const [pts, setPts] = useState([]);              // live stroke for UI
  const drawingRef = useRef(false);
  const ptsRef = useRef([]);                       // mirror for window handlers
  const lastTsRef = useRef(0);
  const captureElRef = useRef(null);               // element we captured on

  // keep mirror in sync
  useEffect(() => {
    ptsRef.current = pts;
  }, [pts]);

  const toLocal = (ev, el) => {
    const target = el || ev.currentTarget || captureElRef.current;
    if (!target) return { x: 0, y: 0 };
    const r = target.getBoundingClientRect();
    return { x: ev.clientX - r.left, y: ev.clientY - r.top };
  };

  const start = (nativeEvent, targetEl) => {
    const p = toLocal(nativeEvent, targetEl);
    drawingRef.current = true;
    captureElRef.current = targetEl || null;

    // pointer capture helps many browsers keep sending events
    try {
      targetEl?.setPointerCapture?.(nativeEvent.pointerId);
    } catch { /* safe no-op */ }

    setPts([p]);
    ptsRef.current = [p];
    lastTsRef.current = performance.now();
  };

  const handleMove = (ev) => {
    if (!drawingRef.current) return;
    const now = performance.now();
    if (now - lastTsRef.current < sampleMs) return;
    lastTsRef.current = now;

    const p = toLocal(ev, captureElRef.current);
    setPts(prev => {
      const next = prev.concat(p);
      ptsRef.current = next;
      return next;
    });
  };

  const endInternal = () => {
    if (!drawingRef.current) return [];
    drawingRef.current = false;

    // release capture
    try {
      captureElRef.current?.releasePointerCapture?.();
    } catch { /* ignore */ }

    const stroke = ptsRef.current;
    captureElRef.current = null;
    return stroke;
  };

  const finish = () => endInternal();

  // window-level listeners so movement is tracked off-element too
  useEffect(() => {
    const onMove = (e) => handleMove(e);
    const onUp = () => endInternal();
    const onCancel = () => endInternal();

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleMs]);

  const move = (nativeEvent) => handleMove(nativeEvent);
  const clear = () => { setPts([]); ptsRef.current = []; };

  return { pts, start, move, finish, clear };
}
